import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaList, FaTh, FaSearch, FaUserTag, FaClock, FaCheckCircle, FaTimesCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getRoleRequests } from '../../../api/services/adminService';
import './RoleRequests.css';

/* ─── helpers ─── */
const ROLE_MAP = { LANDLORD: 'Landlord', BNB_HOST: 'BnB Host', AGENT: 'Agent', GUEST: 'Guest', TENANT: 'Tenant' };
const formatRole = (r) => ROLE_MAP[r] || r || '—';
const formatDate = (ds) => ds ? new Date(ds).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const STATUS_MAP = { PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected', REJECTED_RESUBMIT_ALLOWED: 'rejected' };

const mapItem = (req) => {
  const u = req.user || {};
  const roles = Array.isArray(u.roles) ? u.roles : [];
  const primaryRole = roles.find((r) => r.isPrimary)?.role || roles[0]?.role;
  return {
    id:            req.id,
    userId:        u.id,
    name:          `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—',
    email:         u.email || '—',
    phone:         u.phone || '—',
    currentRole:   formatRole(primaryRole),
    requestedRole: formatRole(req.role),
    submittedDate: formatDate(req.requestedAt),
    status:        STATUS_MAP[(req.status || '').toUpperCase()] || (req.status || '').toLowerCase(),
    reason:        req.reason || null,
    rejectionReason: req.rejectionReason || null,
    reviewedAt:    req.reviewedAt ? formatDate(req.reviewedAt) : null,
    kycStatus:     u.kycStatus || null,
    kybStatus:     u.kybStatus || null,
  };
};

const TABS = [
  { key: 'pending',  label: 'Pending',  icon: <FaClock />,       color: '#d97706' },
  { key: 'approved', label: 'Approved', icon: <FaCheckCircle />, color: '#16a34a' },
  { key: 'rejected', label: 'Rejected', icon: <FaTimesCircle />, color: '#dc2626' },
];

/* ─── Status badge ─── */
const StatusBadge = ({ status }) => {
  const cfg = {
    pending:  { label: 'Pending',  cls: 'rr-badge--pending'  },
    approved: { label: 'Approved', cls: 'rr-badge--approved' },
    rejected: { label: 'Rejected', cls: 'rr-badge--rejected' },
  }[status] || { label: status, cls: '' };
  return <span className={`rr-badge ${cfg.cls}`}>{cfg.label}</span>;
};

const KycBadge = ({ status, label }) => {
  if (!status || status === 'NONE') return <span className="rr-kyc-badge rr-kyc-badge--none">{label}: —</span>;
  if (status === 'APPROVED' || status === 'VERIFIED') return <span className="rr-kyc-badge rr-kyc-badge--ok">{label}: ✓</span>;
  if (status === 'PENDING')  return <span className="rr-kyc-badge rr-kyc-badge--pending">{label}: Pending</span>;
  if (status === 'REJECTED') return <span className="rr-kyc-badge rr-kyc-badge--fail">{label}: ✗</span>;
  return <span className="rr-kyc-badge rr-kyc-badge--none">{label}: {status}</span>;
};

/* ─── Skeleton ─── */
const Skeleton = () => (
  <div className="rr-skeleton-wrap">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="rr-skeleton-row">
        <div className="rr-skeleton-circle" />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="rr-skeleton-line" style={{ width: '40%' }} />
          <div className="rr-skeleton-line" style={{ width: '60%' }} />
        </div>
        <div className="rr-skeleton-line" style={{ width: 80 }} />
      </div>
    ))}
  </div>
);

/* ─── Main ─── */
const RoleRequests = () => {
  const navigate = useNavigate();
  const [tab, setTab]       = useState('pending');
  const [view, setView]     = useState('list');
  const [search, setSearch] = useState('');
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [page, setPage]     = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  const load = useCallback(async (status, pageNum) => {
    setLoading(true); setError(null);
    try {
      const res  = await getRoleRequests(status, pageNum, PAGE_SIZE);
      const data = res?.data || res;
      const content = data?.content || (Array.isArray(data) ? data : []);
      setItems(content.map(mapItem));
      setTotalPages(data?.totalPages || 0);
      setTotalElements(data?.totalElements || content.length);
    } catch (e) { setError(e.message || 'Failed to load role requests'); }
    finally    { setLoading(false); }
  }, []);

  useEffect(() => { setPage(0); setItems([]); }, [tab]);
  useEffect(() => { load(tab, page); }, [tab, page, load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (i) => i.name.toLowerCase().includes(q) || i.email.toLowerCase().includes(q) ||
             i.requestedRole.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="rr-page">
      {/* Header */}
      <div className="rr-page-header">
        <div className="rr-page-header-left">
          <div className="rr-page-icon"><FaUserTag /></div>
          <div>
            <h1 className="rr-page-title">Role Requests</h1>
            <p className="rr-page-sub">Review and manage role upgrade requests</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="rr-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`rr-tab ${tab === t.key ? 'rr-tab--active' : ''}`}
            onClick={() => { setTab(t.key); setSearch(''); }}
          >
            <span className="rr-tab-icon" style={{ color: tab === t.key ? t.color : undefined }}>{t.icon}</span>
            {t.label}
            {tab === t.key && totalElements > 0 && <span className="rr-tab-count">{totalElements}</span>}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="rr-toolbar">
        <div className="rr-search-wrap">
          <FaSearch className="rr-search-icon" />
          <input type="text" className="rr-search" placeholder="Search name, email or requested role…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="rr-view-toggle">
          <button type="button" className={`rr-toggle-btn ${view === 'list' ? 'rr-toggle-btn--active' : ''}`}
            onClick={() => setView('list')} title="List view"><FaList /></button>
          <button type="button" className={`rr-toggle-btn ${view === 'grid' ? 'rr-toggle-btn--active' : ''}`}
            onClick={() => setView('grid')} title="Grid view"><FaTh /></button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rr-alert rr-alert--error">
          {error}
          <button type="button" className="rr-alert-retry" onClick={() => load(tab, page)}>Retry</button>
        </div>
      )}

      {/* Content */}
      {loading ? <Skeleton /> : filtered.length === 0 ? (
        <div className="rr-empty">
          <div className="rr-empty-icon">📋</div>
          <div className="rr-empty-title">No {tab} requests</div>
          <div className="rr-empty-sub">{search ? 'No results match your search.' : `There are no ${tab} role requests right now.`}</div>
        </div>
      ) : view === 'list' ? (
        <RRListView items={filtered} onView={(id) => navigate(`/system-admin/role-requests/${id}`)} />
      ) : (
        <RRGridView items={filtered} onView={(id) => navigate(`/system-admin/role-requests/${id}`)} />
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="rr-pagination">
          <button type="button" className="rr-page-btn" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            <FaChevronLeft /> Prev
          </button>
          <span className="rr-page-info">Page {page + 1} of {totalPages} · {totalElements} total</span>
          <button type="button" className="rr-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            Next <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── List ─── */
const RRListView = ({ items, onView }) => (
  <div className="rr-list-wrap">
    <table className="rr-table">
      <thead>
        <tr>
          <th>Applicant</th>
          <th>Current Role</th>
          <th>Requested Role</th>
          <th>KYC / KYB</th>
          <th>Submitted</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id} className="rr-table-row" onClick={() => onView(item.id)}>
            <td>
              <div className="rr-applicant">
                <div className="rr-avatar">{(item.name || '?')[0].toUpperCase()}</div>
                <div>
                  <div className="rr-applicant-name">{item.name}</div>
                  <div className="rr-applicant-email">{item.email}</div>
                </div>
              </div>
            </td>
            <td className="rr-td-muted">{item.currentRole}</td>
            <td><span className="rr-role-chip">{item.requestedRole}</span></td>
            <td>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <KycBadge status={item.kycStatus} label="KYC" />
                <KycBadge status={item.kybStatus} label="KYB" />
              </div>
            </td>
            <td className="rr-td-muted">{item.submittedDate}</td>
            <td><StatusBadge status={item.status} /></td>
            <td>
              <button type="button" className="rr-view-btn" onClick={(e) => { e.stopPropagation(); onView(item.id); }}>
                View Details
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─── Grid ─── */
const RRGridView = ({ items, onView }) => (
  <div className="rr-grid">
    {items.map((item) => (
      <div key={item.id} className="rr-grid-card" onClick={() => onView(item.id)}>
        <div className="rr-grid-card-top">
          <div className="rr-avatar rr-avatar--lg">{(item.name || '?')[0].toUpperCase()}</div>
          <StatusBadge status={item.status} />
        </div>
        <div className="rr-grid-name">{item.name}</div>
        <div className="rr-grid-email">{item.email}</div>
        <div className="rr-grid-roles">
          <span className="rr-td-muted">{item.currentRole}</span>
          <span className="rr-grid-arrow">→</span>
          <span className="rr-role-chip">{item.requestedRole}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <KycBadge status={item.kycStatus} label="KYC" />
          <KycBadge status={item.kybStatus} label="KYB" />
        </div>
        <div className="rr-grid-date">Submitted {item.submittedDate}</div>
        {item.reason && <div className="rr-grid-reason">{item.reason}</div>}
        <button type="button" className="rr-view-btn rr-view-btn--full"
          onClick={(e) => { e.stopPropagation(); onView(item.id); }}>
          View Details
        </button>
      </div>
    ))}
  </div>
);

export default RoleRequests;
